import React from "react";
import { TEAM_SHORT } from "../../theme/brandTheme";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  LabelList,
  ReferenceLine,
} from "recharts";
import {
  T,
  CHART_AXIS_TICK,
  CHART_LEGEND_STYLE,
  CHART_GRID,
  ChartPanel,
  ChartTooltip,
  EmptyChart,
} from "./tpUi";

/* ═══════════════════════════════════════════════════════════════
   Chart components for the Team Performance redesign.
   Pure presentational — take already-aggregated data as props and
   render Recharts inside ChartPanel. FS = orange, OP = cyan.
   ═══════════════════════════════════════════════════════════════ */

const FS_COLOR = T.primary;       // FS / us
const OP_COLOR = T.accent;        // opponent
const ROLE_LABEL = { DSL: "DSL", JUG: "JUG", MID: "MID", ADL: "ADL", ROAM: "SUP" };
const ROLE_COLORS = { DSL: "#ff1026", JUG: "#00d084", MID: "#20d3ee", ADL: "#ffd166", ROAM: "#a78bfa" };

const wrColor = (wr) => (wr >= 60 ? T.success : wr >= 45 ? T.warn : T.danger);
const pct1 = (v) => (Number.isFinite(v) ? `${v.toFixed(1)}%` : "-");

/* small caption under a chart */
function ChartHint({ children }) {
  return <div style={{ color: T.muted, fontSize: 11, marginTop: 8, lineHeight: 1.4 }}>{children}</div>;
}

/* ── Win rate by side (Blue / Red) ─────────────────────────────── */
export function SideWinRateChart({ side }) {
  const data = [
    { name: "Blue", wr: side?.Blue?.wr ?? 0, games: side?.Blue?.games ?? 0, fill: "#3b82f6" },
    { name: "Red", wr: side?.Red?.wr ?? 0, games: side?.Red?.games ?? 0, fill: "#ef4444" },
  ];
  if (!data.some((d) => d.games > 0)) return <ChartPanel><EmptyChart /></ChartPanel>;
  return (
    <ChartPanel minHeight={240}>
      <ResponsiveContainer width="100%" height={210}>
        <BarChart data={data} margin={{ top: 24, right: 16, left: -8, bottom: 4 }}>
          <CartesianGrid {...CHART_GRID} vertical={false} />
          <XAxis dataKey="name" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} unit="%" width={42} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltip formatter={(v, n) => (n === "wr" ? pct1(v) : v)} />} />
          <ReferenceLine y={50} stroke={T.muted} strokeDasharray="4 6" />
          <Bar dataKey="wr" name="Win rate" radius={[6, 6, 0, 0]} maxBarSize={90}>
            {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
            <LabelList dataKey="wr" position="top" formatter={(v) => pct1(v)} style={{ fill: T.text, fontWeight: 800, fontSize: 13 }} />
            <LabelList dataKey="games" position="insideBottom" formatter={(v) => `${v} เกม`} style={{ fill: "rgba(255,255,255,0.85)", fontSize: 11 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ChartHint>อัตราชนะแยกตามฝั่งแมพ (Blue / Red) · เส้นประ = 50%</ChartHint>
    </ChartPanel>
  );
}

/* ── Scrim vs Match day win rate ───────────────────────────────── */
export function ScrimVsOfficialChart({ buckets }) {
  const data = [
    { name: "ซ้อม (Scrim)", wr: buckets?.scrim?.wr ?? 0, games: buckets?.scrim?.games ?? 0, fill: OP_COLOR },
    { name: "แข่งจริง (Match)", wr: buckets?.official?.wr ?? 0, games: buckets?.official?.games ?? 0, fill: FS_COLOR },
  ];
  if (!data.some((d) => d.games > 0)) return <ChartPanel><EmptyChart /></ChartPanel>;
  const gap = (buckets?.scrim?.wr ?? 0) - (buckets?.official?.wr ?? 0);
  return (
    <ChartPanel minHeight={240}>
      <ResponsiveContainer width="100%" height={210}>
        <BarChart data={data} margin={{ top: 24, right: 16, left: -8, bottom: 4 }}>
          <CartesianGrid {...CHART_GRID} vertical={false} />
          <XAxis dataKey="name" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} unit="%" width={42} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltip formatter={(v, n) => (n === "wr" ? pct1(v) : v)} />} />
          <ReferenceLine y={50} stroke={T.muted} strokeDasharray="4 6" />
          <Bar dataKey="wr" name="Win rate" radius={[6, 6, 0, 0]} maxBarSize={90}>
            {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
            <LabelList dataKey="wr" position="top" formatter={(v) => pct1(v)} style={{ fill: T.text, fontWeight: 800, fontSize: 13 }} />
            <LabelList dataKey="games" position="insideBottom" formatter={(v) => `${v} เกม`} style={{ fill: "rgba(255,255,255,0.85)", fontSize: 11 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ChartHint>
        {Number.isFinite(gap) && Math.abs(gap) >= 1
          ? <span style={{ color: gap > 0 ? T.danger : T.success }}>
              {gap > 0 ? `⚠ แข่งจริงต่ำกว่าซ้อม ${gap.toFixed(1)}% — ฟอร์มหลุดในวันแข่ง` : `แข่งจริงดีกว่าซ้อม ${Math.abs(gap).toFixed(1)}%`}
            </span>
          : "ฟอร์มซ้อมกับแข่งจริงใกล้เคียงกัน"}
      </ChartHint>
    </ChartPanel>
  );
}

/* ── Game length distribution + win rate by length ─────────────── */
export function GameLengthHistogram({ gameLength }) {
  const data = gameLength?.distribution || [];
  if (!data.length || !gameLength?.sampled) return <ChartPanel><EmptyChart /></ChartPanel>;
  return (
    <ChartPanel minHeight={280}>
      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart data={data} margin={{ top: 24, right: 8, left: -8, bottom: 4 }}>
          <CartesianGrid {...CHART_GRID} vertical={false} />
          <XAxis dataKey="bucket" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis yAxisId="g" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} width={36} />
          <YAxis yAxisId="wr" orientation="right" domain={[0, 100]} tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} unit="%" width={42} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltip formatter={(v, n) => (n === "อัตราชนะ" ? pct1(v) : `${v} เกม`)} />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <Bar yAxisId="g" dataKey="games" name="จำนวนเกม" fill={OP_COLOR} radius={[6, 6, 0, 0]} maxBarSize={64}>
            <LabelList dataKey="games" position="top" style={{ fill: T.muted, fontSize: 11 }} />
          </Bar>
          <Line yAxisId="wr" type="monotone" dataKey="wr" name="อัตราชนะ" stroke={FS_COLOR} strokeWidth={3} dot={{ r: 4, fill: FS_COLOR }} />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartHint>กราฟแท่ง = จำนวนเกมตามความยาว · เส้น = อัตราชนะของช่วงนั้น · เฉลี่ย {gameLength.avg}′ · กลาง {gameLength.median}′</ChartHint>
    </ChartPanel>
  );
}

/* ── Win-rate trend by week ─────────────────────────────────────── */
export function WinRateTrendChart({ trend, showSplit = true }) {
  const data = trend || [];
  if (!data.length) return <ChartPanel><EmptyChart /></ChartPanel>;
  return (
    <ChartPanel minHeight={280}>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={data} margin={{ top: 20, right: 16, left: -8, bottom: 4 }}>
          <CartesianGrid {...CHART_GRID} vertical={false} />
          <XAxis dataKey="week" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} unit="%" width={42} />
          <Tooltip content={<ChartTooltip formatter={(v) => pct1(v)} />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <ReferenceLine y={50} stroke={T.muted} strokeDasharray="4 6" />
          <Line type="monotone" dataKey="wr" name="รวม" stroke={FS_COLOR} strokeWidth={3} dot={{ r: 4, fill: FS_COLOR }} connectNulls />
          {showSplit && <Line type="monotone" dataKey="scrimWr" name="ซ้อม" stroke={OP_COLOR} strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls />}
          {showSplit && <Line type="monotone" dataKey="officialWr" name="แข่งจริง" stroke={T.warn} strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls />}
        </LineChart>
      </ResponsiveContainer>
      <ChartHint>อัตราชนะรายสัปดาห์ · เส้นประ = แยกซ้อม/แข่งจริง · เส้นประแนวนอน = 50%</ChartHint>
    </ChartPanel>
  );
}

/* ── Per-role FS vs OP radar ────────────────────────────────────── */
const ROLE_METRICS = {
  kda: { key: "kda", label: "KDA" },
  gpm: { key: "gpm", label: "GPM" },
  gold: { key: "gold", label: "Gold เฉลี่ย" },
  dmgShare: { key: "dmgShare", label: "% ดาเมจ" },
};

export function RolePerformanceRadar({ roles, metric = "kda" }) {
  const m = ROLE_METRICS[metric] || ROLE_METRICS.kda;
  const data = (roles || []).map((r) => ({
    role: ROLE_LABEL[r.role] || r.role,
    FS: r.fs?.[m.key] ?? 0,
    OP: r.op?.[m.key] ?? 0,
  }));
  if (!data.length) return <ChartPanel><EmptyChart /></ChartPanel>;
  return (
    <ChartPanel minHeight={300}>
      <ResponsiveContainer width="100%" height={280}>
        <RadarChart data={data} outerRadius="72%">
          <PolarGrid stroke="rgba(143,240,255,0.18)" />
          <PolarAngleAxis dataKey="role" tick={{ ...CHART_AXIS_TICK, fontSize: 13 }} />
          <PolarRadiusAxis tick={{ fill: T.muted, fontSize: 10 }} axisLine={false} />
          <Tooltip content={<ChartTooltip formatter={(v) => (Number.isFinite(v) ? v.toFixed(metric === "kda" || metric === "dmgShare" ? 1 : 0) : "-")} />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <Radar name={TEAM_SHORT} dataKey="FS" stroke={FS_COLOR} fill={FS_COLOR} fillOpacity={0.32} />
          <Radar name="OP" dataKey="OP" stroke={OP_COLOR} fill={OP_COLOR} fillOpacity={0.18} />
        </RadarChart>
      </ResponsiveContainer>
      <ChartHint>เทียบ {m.label} รายตำแหน่ง {TEAM_SHORT} (แดง) vs คู่แข่ง (ฟ้า) · ยิ่งกว้าง = ยิ่งเหนือกว่า</ChartHint>
    </ChartPanel>
  );
}

/* ── Per-role FS vs OP grouped bars (one metric) ───────────────── */
export function RoleGroupedBars({ roles, metric = "gpm" }) {
  const m = ROLE_METRICS[metric] || ROLE_METRICS.gpm;
  const data = (roles || []).map((r) => ({
    role: ROLE_LABEL[r.role] || r.role,
    FS: r.fs?.[m.key] ?? 0,
    OP: r.op?.[m.key] ?? 0,
  }));
  if (!data.length) return <ChartPanel><EmptyChart /></ChartPanel>;
  const dec = metric === "kda" || metric === "dmgShare" ? 1 : 0;
  return (
    <ChartPanel minHeight={300}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 24, right: 8, left: -8, bottom: 4 }}>
          <CartesianGrid {...CHART_GRID} vertical={false} />
          <XAxis dataKey="role" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} width={44} unit={metric === "dmgShare" ? "%" : ""} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltip formatter={(v) => (Number.isFinite(v) ? v.toFixed(dec) : "-")} />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <Bar dataKey="FS" name={TEAM_SHORT} fill={FS_COLOR} radius={[5, 5, 0, 0]} maxBarSize={42}>
            <LabelList dataKey="FS" position="top" formatter={(v) => (Number.isFinite(v) ? v.toFixed(dec) : "")} style={{ fill: T.muted, fontSize: 10 }} />
          </Bar>
          <Bar dataKey="OP" name="คู่แข่ง" fill={OP_COLOR} radius={[5, 5, 0, 0]} maxBarSize={42} />
        </BarChart>
      </ResponsiveContainer>
      <ChartHint>{m.label} รายตำแหน่ง — {TEAM_SHORT} (แดง) เทียบคู่แข่ง (ฟ้า)</ChartHint>
    </ChartPanel>
  );
}

/* ── Damage share distribution (100% stacked, FS vs OP) ────────── */
export function RoleDamageShareChart({ roles }) {
  if (!roles?.length) return <ChartPanel><EmptyChart /></ChartPanel>;
  const ROLE_KEYS = roles.map((r) => r.role);
  const row = (sideKey, label) => {
    const o = { side: label };
    roles.forEach((r) => { o[r.role] = r[sideKey]?.dmgShare ?? 0; });
    return o;
  };
  const data = [row("fs", "FS"), row("op", "คู่แข่ง")];
  const hasData = roles.some((r) => Number.isFinite(r.fs?.dmgShare));
  if (!hasData) return <ChartPanel><EmptyChart /></ChartPanel>;
  return (
    <ChartPanel minHeight={220}>
      <ResponsiveContainer width="100%" height={190}>
        <BarChart layout="vertical" data={data} stackOffset="expand" margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
          <XAxis type="number" domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="side" tick={{ ...CHART_AXIS_TICK, fontWeight: 800 }} axisLine={false} tickLine={false} width={64} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltip formatter={(v, n) => `${ROLE_LABEL[n] || n}: ${pct1(v)}`} />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} formatter={(v) => ROLE_LABEL[v] || v} />
          {ROLE_KEYS.map((rk) => (
            <Bar key={rk} dataKey={rk} name={rk} stackId="dmg" fill={ROLE_COLORS[rk] || T.primary} maxBarSize={46}>
              <LabelList dataKey={rk} position="center" formatter={(v) => (v >= 8 ? `${Math.round(v)}` : "")} style={{ fill: "#0b0d18", fontSize: 11, fontWeight: 800 }} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      <ChartHint>สัดส่วนดาเมจที่แต่ละตำแหน่งทำได้ (รวม = 100%) · เทียบการกระจายดาเมจของ {TEAM_SHORT} กับคู่แข่ง</ChartHint>
    </ChartPanel>
  );
}

/* ── Draft: pick / ban counts (top heroes, horizontal) ─────────── */
export function DraftPickBanChart({ heroes, limit = 12 }) {
  const data = (heroes || [])
    .slice()
    .sort((a, b) => (b.pick + b.ban) - (a.pick + a.ban))
    .slice(0, limit)
    .map((h) => ({ name: h.hero, pick: h.pick || 0, ban: h.ban || 0 }));
  if (!data.length) return <ChartPanel><EmptyChart /></ChartPanel>;
  return (
    <ChartPanel minHeight={Math.max(240, data.length * 28 + 60)}>
      <ResponsiveContainer width="100%" height={Math.max(220, data.length * 28 + 40)}>
        <BarChart layout="vertical" data={data} margin={{ top: 8, right: 24, left: 8, bottom: 4 }}>
          <CartesianGrid {...CHART_GRID} horizontal={false} />
          <XAxis type="number" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
          <YAxis type="category" dataKey="name" tick={{ ...CHART_AXIS_TICK, fontSize: 12 }} axisLine={false} tickLine={false} width={96} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltip />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <Bar dataKey="pick" name="Pick" fill={FS_COLOR} radius={[0, 4, 4, 0]} maxBarSize={18} />
          <Bar dataKey="ban" name="Ban" fill={T.danger} radius={[0, 4, 4, 0]} maxBarSize={18} />
        </BarChart>
      </ResponsiveContainer>
      <ChartHint>ฮีโร่ที่ pick/ban บ่อยที่สุด · เรียงตามการมีตัวตนรวม (pick + ban)</ChartHint>
    </ChartPanel>
  );
}

/* ── Draft: win rate by hero (min games, horizontal, colored) ──── */
export function DraftWinRateChart({ heroes, minPick = 3, limit = 12 }) {
  const data = (heroes || [])
    .filter((h) => (h.pick || 0) >= minPick)
    .slice()
    .sort((a, b) => (b.winRate ?? 0) - (a.winRate ?? 0))
    .slice(0, limit)
    .map((h) => ({ name: h.hero, winRate: h.winRate ?? 0, pick: h.pick || 0 }));
  if (!data.length) return <ChartPanel><EmptyChart label={`ยังไม่มีฮีโร่ที่ pick ≥ ${minPick} เกม`} /></ChartPanel>;
  return (
    <ChartPanel minHeight={Math.max(240, data.length * 28 + 60)}>
      <ResponsiveContainer width="100%" height={Math.max(220, data.length * 28 + 40)}>
        <BarChart layout="vertical" data={data} margin={{ top: 8, right: 40, left: 8, bottom: 4 }}>
          <CartesianGrid {...CHART_GRID} horizontal={false} />
          <XAxis type="number" domain={[0, 100]} unit="%" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" tick={{ ...CHART_AXIS_TICK, fontSize: 12 }} axisLine={false} tickLine={false} width={96} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltip formatter={(v, n) => (n === "winRate" ? pct1(v) : v)} />} />
          <ReferenceLine x={50} stroke={T.muted} strokeDasharray="4 6" />
          <Bar dataKey="winRate" name="Win rate" radius={[0, 4, 4, 0]} maxBarSize={20}>
            {data.map((d, i) => <Cell key={i} fill={wrColor(d.winRate)} />)}
            <LabelList dataKey="winRate" position="right" formatter={(v) => pct1(v)} style={{ fill: T.text, fontSize: 11, fontWeight: 700 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ChartHint>อัตราชนะต่อฮีโร่ (เฉพาะที่ pick ≥ {minPick} เกม) · เขียว ≥60% · แดง &lt;45%</ChartHint>
    </ChartPanel>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Early Game Macro charts — consume aggregateEarlyGameMacro output
   (byMark[t][metric] time-series at 4'/8'/12'/15').
   ═══════════════════════════════════════════════════════════════ */
const MACRO_MARKS = [4, 8, 12, 15];

/* FS vs OP average of a macro metric across the four time marks. */
export function MacroTimeSeriesChart({ byMark, metricKey, unit = "", hint = "" }) {
  const data = MACRO_MARKS.map((t) => {
    const m = byMark?.[t]?.[metricKey] || {};
    return { time: `${t}'`, FS: +(m.fsAvg ?? 0).toFixed(2), OP: +(m.opAvg ?? 0).toFixed(2), sampled: m.sampled ?? 0 };
  });
  if (!data.some((d) => d.sampled > 0)) return <ChartPanel><EmptyChart /></ChartPanel>;
  const fmtV = (v) => (Number.isFinite(v) ? `${v.toFixed(unit === "k" ? 1 : 2)}${unit}` : "-");
  return (
    <ChartPanel minHeight={260}>
      <ResponsiveContainer width="100%" height={230}>
        <LineChart data={data} margin={{ top: 20, right: 16, left: -8, bottom: 4 }}>
          <CartesianGrid {...CHART_GRID} vertical={false} />
          <XAxis dataKey="time" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} width={44} unit={unit} />
          <Tooltip content={<ChartTooltip formatter={(v) => fmtV(v)} />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <Line type="monotone" dataKey="FS" name={TEAM_SHORT} stroke={FS_COLOR} strokeWidth={3} dot={{ r: 4, fill: FS_COLOR }}>
            <LabelList dataKey="FS" position="top" formatter={(v) => fmtV(v)} style={{ fill: FS_COLOR, fontSize: 11, fontWeight: 700 }} />
          </Line>
          <Line type="monotone" dataKey="OP" name="คู่แข่ง" stroke={OP_COLOR} strokeWidth={2.5} strokeDasharray="5 4" dot={{ r: 3, fill: OP_COLOR }} />
        </LineChart>
      </ResponsiveContainer>
      <ChartHint>{hint || `ค่าเฉลี่ย ${TEAM_SHORT} (แดง) เทียบคู่แข่ง (ฟ้า) ที่นาที 4 / 8 / 12 / 15`}</ChartHint>
    </ChartPanel>
  );
}

/* Who controlled each window — 100% stacked lead split per time mark. */
export function MacroLeadSplitChart({ byMark, metricKey }) {
  const data = MACRO_MARKS.map((t) => {
    const m = byMark?.[t]?.[metricKey] || {};
    return { time: `${t}'`, FS: +(m.fsLeadPct ?? 0).toFixed(1), Tie: +(m.tiePct ?? 0).toFixed(1), OP: +(m.opLeadPct ?? 0).toFixed(1), sampled: m.sampled ?? 0 };
  });
  if (!data.some((d) => d.sampled > 0)) return <ChartPanel><EmptyChart /></ChartPanel>;
  return (
    <ChartPanel minHeight={260}>
      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={data} margin={{ top: 12, right: 16, left: -8, bottom: 4 }}>
          <CartesianGrid {...CHART_GRID} vertical={false} />
          <XAxis dataKey="time" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} unit="%" width={44} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltip formatter={(v) => pct1(v)} />} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <Bar dataKey="FS" name={`${TEAM_SHORT} นำ`} stackId="lead" fill={FS_COLOR} maxBarSize={60} />
          <Bar dataKey="Tie" name="เสมอ" stackId="lead" fill={T.muted} maxBarSize={60} />
          <Bar dataKey="OP" name="คู่แข่งนำ" stackId="lead" fill={OP_COLOR} maxBarSize={60} />
        </BarChart>
      </ResponsiveContainer>
      <ChartHint>สัดส่วนเกมที่ {TEAM_SHORT} นำ / เสมอ / ตาม ในแต่ละช่วงเวลา (อิงคอลัมน์ AVT)</ChartHint>
    </ChartPanel>
  );
}

/* Per-opponent early-game win rate (horizontal, colored). */
export function MacroOpponentBars({ byOpponent, limit = 12 }) {
  const data = (byOpponent || [])
    .slice(0, limit)
    .map((o) => ({ name: o.opponent, wr: +(o.wr ?? 0).toFixed(1), games: o.games || 0 }));
  if (!data.length) return <ChartPanel><EmptyChart /></ChartPanel>;
  return (
    <ChartPanel minHeight={Math.max(240, data.length * 26 + 60)}>
      <ResponsiveContainer width="100%" height={Math.max(220, data.length * 26 + 40)}>
        <BarChart layout="vertical" data={data} margin={{ top: 8, right: 44, left: 8, bottom: 4 }}>
          <CartesianGrid {...CHART_GRID} horizontal={false} />
          <XAxis type="number" domain={[0, 100]} unit="%" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" tick={{ ...CHART_AXIS_TICK, fontSize: 12 }} axisLine={false} tickLine={false} width={84} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltip formatter={(v, n) => (n === "wr" ? pct1(v) : v)} />} />
          <ReferenceLine x={50} stroke={T.muted} strokeDasharray="4 6" />
          <Bar dataKey="wr" name={`${TEAM_SHORT} WR`} radius={[0, 4, 4, 0]} maxBarSize={18}>
            {data.map((d, i) => <Cell key={i} fill={wrColor(d.wr)} />)}
            <LabelList dataKey="games" position="right" formatter={(v) => `${v} เกม`} style={{ fill: T.muted, fontSize: 10 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ChartHint>อัตราชนะ {TEAM_SHORT} แยกตามคู่แข่ง · ใช้ดูว่าทีมไหน early game ลำบาก</ChartHint>
    </ChartPanel>
  );
}
